/**
 * Price staleness and comparison utilities for flight/room options.
 *
 * The pure comparison rules (`getPriceDelta`, `getOriginalCost`, `PriceDelta`) live in
 * `@elara/domain/pricing` and are re-exported here so existing web call sites are unchanged.
 * The two predicates that must run **offline against `now()`** — `isPriceStale` and
 * `isTravelDatePassed` — stay client-side; they can't be denormalized because the answer
 * changes as the clock advances.
 */

import type { RoomRatePricing } from '@elara/domain/models/schemas/room-schemas';

import { STALE_THRESHOLD_MS } from '@elara/domain/constants';
import { parseDateOnlyLocal } from '@elara/domain/date-utils';

export { getOriginalCost, getPriceDelta } from '@elara/domain/pricing';
export type { PriceDelta } from '@elara/domain/pricing';

/**
 * Returns true if `fetchedAt` is older than {@link STALE_THRESHOLD_MS}.
 *
 * This is only the time-threshold half of the price-recheck rule. The full predicate is
 * **compound**: a price is worth rechecking when `isPriceStale(fetchedAt)` AND the travel date
 * has not already passed (see each model's `isStale` getter, which short-circuits on
 * `isTravelDatePassed`). Kept client-side and computed on read against `now()` — never stored.
 */
export function isPriceStale(fetchedAt?: Date | { toDate?: () => Date } | null): boolean {
  if (!fetchedAt) return true;
  const date = typeof fetchedAt === 'object' && 'toDate' in fetchedAt && fetchedAt.toDate
    ? fetchedAt.toDate()
    : fetchedAt as Date;
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return true;
  return Date.now() - date.getTime() > STALE_THRESHOLD_MS;
}

/**
 * Returns true if the given travel date (YYYY-MM-DD or ISO string) is today or in the past.
 *
 * Parses the date-only part to **local** start-of-day via `parseDateOnlyLocal` so the
 * day-boundary comparison matches the rest of the web client (and any future native client) —
 * see that helper for the UTC-parsing trap this avoids.
 */
export function isTravelDatePassed(dateStr?: string | null): boolean {
  if (!dateStr) return false;
  const travelDate = parseDateOnlyLocal(dateStr);
  if (!travelDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return travelDate.getTime() <= today.getTime();
}

/** Short note about whether the displayed price already includes taxes/fees. Returns undefined when both are unknown. */
export function pricingInclusionNote(
  pricing: Pick<RoomRatePricing, 'taxesIncluded' | 'feesIncluded'>,
): string | undefined {
  const { taxesIncluded: tax, feesIncluded: fee } = pricing;
  if (tax === true && fee === true) return 'Includes taxes and fees';
  if (tax === true) return fee === false ? 'Includes taxes, excludes fees' : 'Includes taxes';
  if (fee === true) return tax === false ? 'Includes fees, excludes taxes' : 'Includes fees';
  if (tax === false && fee === false) return 'Excludes taxes and fees';
  if (tax === false) return 'Excludes taxes';
  if (fee === false) return 'Excludes fees';
  return undefined;
}
